/**
 * BACK-01..BACK-13 — pilha única de overlays (diálogos, folhas, menus, prévias) sincronizada com
 * o histórico do navegador. Sem React: componentes usam `useOverlay`; a ponte
 * (`OverlayHistoryBridge`) informa trocas de rota e faz o `bootstrap` no carregamento.
 *
 * Regras (ver `.specs/features/synap-design-final-sketch-010-2026-10/design.md` §A.4–A.6):
 * - uma entrada de histórico por overlay vivo, mesma URL, estado do React Router preservado
 *   (`usr/key/idx`) + marcador `synapOverlay: { id, depth, baseKey }`;
 * - reconciliação em microtask (StrictMode / abrir-fechar no mesmo tick não mexe no histórico);
 * - recuos do próprio gerenciador (`go(-n)`) e pops de overlay são engolidos antes do Router;
 * - entradas fantasma (overlay já fechado) são puladas.
 */
import { useToastStore } from '@/shared/ui/toast/toastStore'
import { browserHistoryAdapter, type HistoryAdapter } from './historyAdapter'

export type OverlayKind =
  | 'dialog'
  | 'alertdialog'
  | 'sheet'
  | 'drawer'
  | 'menu'
  | 'popover'
  | 'preview'
  | 'palette'

export type OverlayCloseReason = 'back' | 'escape' | 'scrim' | 'ui' | 'route-change' | 'parent-closed'

export interface OverlayOptions {
  kind: OverlayKind
  /** O dono põe `open=false` (o gerenciador já tirou o overlay da pilha). */
  onRequestClose: (reason: OverlayCloseReason) => void
  /** Guarda de descarte (BACK-10): `false` (ou exceção) mantém o overlay aberto. */
  canDismiss?: () => boolean | Promise<boolean>
  /** `false` = não fecha por voltar/Esc (BACK-13); mostra `nonDismissibleMessage`. */
  isDismissible?: () => boolean
  nonDismissibleMessage?: string
  /** Padrão true. */
  closeOnEscape?: boolean
  /** Padrão true. */
  closeOnRouteChange?: boolean
  /** Rótulo para depuração. */
  label?: string
  /** Id estável opcional (o hook usa `useId`); padrão gerado. */
  id?: string
}

export interface OverlayHandle {
  readonly id: string
  update(options: Partial<OverlayOptions>): void
  /** Fechamento pela UI/dono (BACK-05/06): tira este e os de cima da pilha. Idempotente. */
  release(): void
}

export interface OverlayTop {
  readonly id: string
  readonly options: Readonly<OverlayOptions>
}

export type OverlayPopResult = 'overlay' | 'silent' | 'ghost' | 'route'

export interface OverlayManager {
  register(options: OverlayOptions): OverlayHandle
  /** `true` = havia overlay (o evento foi consumido), mesmo que ele continue aberto. */
  requestCloseTop(reason: OverlayCloseReason): Promise<boolean>
  /** BACK-07: fecha todos, consome as entradas de histórico e só então roda `cb`. */
  closeAllThen(cb: () => void, opts?: { timeoutMs?: number }): void
  /** Como `closeAllThen`, mas só do overlay `id` para cima (se já saiu, só espera o recuo). */
  closeThen(id: string, cb: () => void, opts?: { timeoutMs?: number }): void
  size(): number
  isTop(id: string): boolean
  peekTop(): OverlayTop | null
  subscribe(listener: () => void): () => void
  /** Instala o listener de `popstate` (captura). Idempotente. */
  connect(): void
  handlePopState(event: PopStateEvent): OverlayPopResult
  handleRouteChange(routeKey: string): void
  /** Carregamento com entrada de overlay no topo (reload): recua até a base. Idempotente. */
  bootstrap(): void
}

export interface OverlayManagerDeps {
  toast: (message: string) => void
  /** Fallback quando o `popstate` do recuo não chega (padrão 400 ms, como `popMenuHistoryThen`). */
  silentPopTimeoutMs?: number
}

export const OVERLAY_HISTORY_KEY = 'synapOverlay'
export const DEFAULT_NON_DISMISSIBLE_MESSAGE = 'Aguarde terminar para fechar.'
const DEFAULT_ROUTE_KEY = 'default'

interface OverlayHistoryMarker {
  id: string
  depth: number
  baseKey: string
}

interface Entry {
  id: string
  options: OverlayOptions
  guard: Promise<boolean> | null
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

/** Mesmo default do React Router (`state.key || "default"`). */
const readRouteKey = (state: unknown): string =>
  isRecord(state) && typeof state.key === 'string' && state.key ? state.key : DEFAULT_ROUTE_KEY

const readMarker = (state: unknown): OverlayHistoryMarker | null => {
  if (!isRecord(state)) return null
  const marker = state[OVERLAY_HISTORY_KEY]
  if (!isRecord(marker) || typeof marker.id !== 'string' || typeof marker.depth !== 'number') {
    return null
  }
  return {
    id: marker.id,
    depth: marker.depth,
    baseKey: typeof marker.baseKey === 'string' ? marker.baseKey : DEFAULT_ROUTE_KEY,
  }
}

/** Só o que o React Router lê do estado; marcadores antigos não são copiados. */
const routerStateOf = (state: unknown): Record<string, unknown> => {
  if (!isRecord(state)) return {}
  const copy: Record<string, unknown> = {}
  for (const field of ['usr', 'key', 'idx'] as const) {
    if (field in state) copy[field] = state[field]
  }
  return copy
}

export const createOverlayManager = (
  history: HistoryAdapter,
  deps: OverlayManagerDeps,
): OverlayManager => {
  const silentPopTimeoutMs = deps.silentPopTimeoutMs ?? 400
  let stack: Entry[] = []
  /** Entradas nossas acima da base, como estão (ou ficarão) no histórico. */
  let historyDepth = 0
  /** Entrada sobre a qual o primeiro overlay foi empilhado. */
  let base: { key: string; markerId: string | null } | null = null
  /** Chave da rota que o Router está mostrando (informada pela ponte). */
  let currentRouteKey: string | null = null
  let inFlight = false
  let inFlightTimer: ReturnType<typeof setTimeout> | null = null
  let afterSettle: Array<() => void> = []
  let reconcileScheduled = false
  let disconnect: (() => void) | null = null
  let bootstrapped = false
  let idCounter = 0
  const listeners = new Set<() => void>()

  const notify = () => {
    for (const listener of Array.from(listeners)) listener()
  }

  const goSilently = (delta: number, timeoutMs = silentPopTimeoutMs) => {
    inFlight = true
    if (inFlightTimer) clearTimeout(inFlightTimer)
    inFlightTimer = setTimeout(finishInFlight, timeoutMs)
    history.go(delta)
  }

  const reconcile = (timeoutMs?: number) => {
    if (inFlight) return
    const target = stack.length
    if (historyDepth < target) {
      if (historyDepth === 0) {
        const current = history.getState()
        base = { key: readRouteKey(current), markerId: readMarker(current)?.id ?? null }
      }
      while (historyDepth < target) {
        const entry = stack[historyDepth]
        const marker: OverlayHistoryMarker = {
          id: entry.id,
          depth: historyDepth + 1,
          baseKey: base?.key ?? DEFAULT_ROUTE_KEY,
        }
        history.pushState({ ...routerStateOf(history.getState()), [OVERLAY_HISTORY_KEY]: marker })
        historyDepth += 1
      }
      return
    }
    if (historyDepth > target) {
      const delta = historyDepth - target
      historyDepth = target
      goSilently(-delta, timeoutMs)
    }
  }

  /** Reconcilia e, se o histórico ficou estável, roda os callbacks de `closeAllThen`. */
  const settle = (timeoutMs?: number) => {
    reconcile(timeoutMs)
    if (inFlight || afterSettle.length === 0) return
    const callbacks = afterSettle
    afterSettle = []
    for (const cb of callbacks) cb()
  }

  function finishInFlight() {
    if (!inFlight) return
    inFlight = false
    if (inFlightTimer) clearTimeout(inFlightTimer)
    inFlightTimer = null
    settle()
  }

  const scheduleReconcile = () => {
    if (reconcileScheduled) return
    reconcileScheduled = true
    queueMicrotask(() => {
      reconcileScheduled = false
      if (!inFlight) settle()
    })
  }

  /** Tira `entry` e os de cima da pilha; os de cima recebem `parent-closed` (BACK-06). */
  const removeFrom = (entry: Entry): Entry[] | null => {
    const index = stack.indexOf(entry)
    if (index < 0) return null
    const removed = stack.slice(index)
    stack = stack.slice(0, index)
    for (const above of removed.slice(1).reverse()) above.options.onRequestClose('parent-closed')
    return removed
  }

  const closeEntry = (entry: Entry, reason: OverlayCloseReason) => {
    if (!removeFrom(entry)) return
    entry.options.onRequestClose(reason)
    notify()
    scheduleReconcile()
  }

  /** Aplica BACK-13 (não dispensável) e BACK-10 (guarda). Síncrono quando não há guarda. */
  const tryClose = (entry: Entry, reason: OverlayCloseReason): boolean | Promise<boolean> => {
    if (entry.options.isDismissible?.() === false) {
      deps.toast(entry.options.nonDismissibleMessage ?? DEFAULT_NON_DISMISSIBLE_MESSAGE)
      return false
    }
    const canDismiss = entry.options.canDismiss
    if (!canDismiss) {
      closeEntry(entry, reason)
      return true
    }
    if (entry.guard) return entry.guard.then(() => false)
    let verdict: boolean | Promise<boolean>
    try {
      verdict = canDismiss()
    } catch {
      return false
    }
    if (typeof verdict === 'boolean') {
      if (verdict) closeEntry(entry, reason)
      return verdict
    }
    const guard = Promise.resolve(verdict)
      .catch(() => false)
      .then((ok) => {
        entry.guard = null
        if (ok !== true || !stack.includes(entry)) return false
        closeEntry(entry, reason)
        return true
      })
    entry.guard = guard
    return guard
  }

  const release = (entry: Entry) => {
    if (!removeFrom(entry)) return
    notify()
    scheduleReconcile()
  }

  const closeForRouteChange = () => {
    const closing = stack.filter((entry) => entry.options.closeOnRouteChange !== false)
    stack = stack.filter((entry) => entry.options.closeOnRouteChange === false)
    historyDepth = 0
    base = null
    for (const entry of closing.reverse()) entry.options.onRequestClose('route-change')
    if (closing.length > 0) notify()
    scheduleReconcile()
  }

  /** O usuário recuou para a posição `position` (0 = base) desta rota. */
  const closeAbove = (position: number) => {
    historyDepth = position
    const toClose = stack.slice(position).reverse()
    for (const entry of toClose) {
      const result = tryClose(entry, 'back')
      if (result !== true) break
    }
    // Overlay que ficou (guarda/não dispensável) ganha a entrada de volta.
    scheduleReconcile()
  }

  const handlePopState = (event: PopStateEvent): OverlayPopResult => {
    const state = event.state as unknown
    const marker = readMarker(state)
    const key = readRouteKey(state)
    const wasInFlight = inFlight
    const result = classifyPop(marker, key)
    if (wasInFlight) finishInFlight()
    return result
  }

  const classifyPop = (marker: OverlayHistoryMarker | null, key: string): OverlayPopResult => {
    const referenceKey = currentRouteKey ?? base?.key ?? null
    if (referenceKey === null && !marker) return 'route'

    if (referenceKey !== null && key !== referenceKey) {
      if (marker) {
        // Fantasma de outra rota (voltou de uma rota aberta a partir de um overlay): pula até a
        // base dela; esse próximo pop é de rota e chega ao Router.
        history.go(-marker.depth)
        return 'ghost'
      }
      closeForRouteChange()
      currentRouteKey = key
      return 'route'
    }

    // Mesma rota: o Router não precisa ver nada.
    let position: number | null = null
    if (base && (marker?.id ?? null) === base.markerId) {
      position = 0
    } else if (marker) {
      const index = stack.findIndex((entry) => entry.id === marker.id)
      if (index >= 0 && index < historyDepth) position = index + 1
    } else {
      // Entrada da própria rota abaixo da base (ex.: depois de ficar parado num fantasma).
      position = 0
    }

    if (position !== null) {
      if (position < historyDepth) {
        closeAbove(position)
        return 'overlay'
      }
      return 'silent'
    }

    // Fantasma da rota atual (marker sem overlay vivo).
    if (marker && stack.length > 0 && marker.depth > historyDepth) {
      // Avançou para além do topo: volta ao topo.
      goSilently(-(marker.depth - historyDepth))
    } else if (marker && stack.length === 0) {
      // Avançou a partir da base para um fantasma: segue na mesma direção.
      history.go(1)
    } else if (marker) {
      history.go(-marker.depth)
      closeForRouteChange()
    }
    return 'ghost'
  }

  /** BACK-07: fecha da posição `index` para cima (motivo `ui`), recua e só então roda `cb`. */
  const closeFromThen = (index: number, cb: () => void, timeoutMs?: number) => {
    const closing = stack.slice(index).reverse()
    stack = stack.slice(0, index)
    for (const entry of closing) entry.options.onRequestClose('ui')
    if (closing.length > 0) notify()
    afterSettle.push(cb)
    if (!inFlight) settle(timeoutMs)
  }

  const connect = () => {
    if (disconnect) return
    disconnect = history.addPopStateListener((event) => {
      if (handlePopState(event) !== 'route') event.stopImmediatePropagation()
    })
  }

  return {
    register(options) {
      connect()
      const entry: Entry = {
        id: options.id ?? `overlay-${++idCounter}`,
        options,
        guard: null,
      }
      stack.push(entry)
      notify()
      scheduleReconcile()
      return {
        id: entry.id,
        update(next) {
          entry.options = { ...entry.options, ...next }
        },
        release() {
          release(entry)
        },
      }
    },

    async requestCloseTop(reason) {
      const top = stack.at(-1)
      if (!top) return false
      if (reason === 'escape' && top.options.closeOnEscape === false) return false
      await tryClose(top, reason)
      return true
    },

    closeAllThen(cb, opts) {
      closeFromThen(0, cb, opts?.timeoutMs)
    },

    closeThen(id, cb, opts) {
      const index = stack.findIndex((entry) => entry.id === id)
      closeFromThen(index < 0 ? stack.length : index, cb, opts?.timeoutMs)
    },

    size: () => stack.length,

    isTop: (id) => stack.at(-1)?.id === id,

    peekTop() {
      const top = stack.at(-1)
      return top ? { id: top.id, options: top.options } : null
    },

    subscribe(listener) {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },

    connect,

    handlePopState,

    handleRouteChange(routeKey) {
      if (routeKey === currentRouteKey) return
      const firstReport = currentRouteKey === null
      currentRouteKey = routeKey
      if (!firstReport) closeForRouteChange()
    },

    bootstrap() {
      if (bootstrapped) return
      bootstrapped = true
      connect()
      const marker = readMarker(history.getState())
      if (marker && marker.depth > 0 && stack.length === 0 && historyDepth === 0) {
        goSilently(-marker.depth)
      }
    },
  }
}

export const overlayManager: OverlayManager = createOverlayManager(browserHistoryAdapter, {
  toast: (message) => useToastStore.getState().add(message, { variant: 'info' }),
})
